import React, { useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Dropdown, Typography, notification } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  ShopOutlined,
  TagsOutlined,
  InboxOutlined,
  FileTextOutlined,
  UserOutlined,
  CheckCircleOutlined,
  BarChartOutlined,
  LogoutOutlined,
  SettingOutlined,
  BellOutlined,
  BankOutlined,
  CarOutlined,
  CalendarOutlined,
  CrownOutlined
} from '@ant-design/icons';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';

import Dashboard from '../pages/Dashboard';
import ProductList from '../pages/Products/ProductList';
import ProductForm from '../pages/Products/ProductFormNew';
import ProductView from '../pages/Products/ProductView';
import CategoryList from '../pages/Categories/CategoryList';
import CategoryForm from '../pages/Categories/CategoryForm';
import AttributeList from '../pages/Attributes/AttributeList';
import AttributeForm from '../pages/Attributes/AttributeForm';
import AttributeValueManager from '../pages/Attributes/AttributeValueManager';
import StockList from '../pages/Stock/StockList';
import StockForm from '../pages/Stock/StockForm';
import StockDashboard from '../pages/Stock/index';
import InvoiceExportList from '../pages/Invoices/InvoiceExportList';
import InvoiceImportList from '../pages/Invoices/InvoiceImportList';
import InvoiceExportForm from '../pages/Invoices/InvoiceExportForm';
import InvoiceImportForm from '../pages/Invoices/InvoiceImportForm';
import UserList from '../pages/Users/UserList';
import UserForm from '../pages/Users/UserForm';
import UserDetail from '../pages/Users/UserDetail';
import PendingApproval from '../pages/Users/PendingApproval';
import CommissionApproval from '../pages/Approvals/CommissionApproval';
import PackingList from '../pages/Approvals/PackingList';
import WithdrawalList from '../pages/Withdrawals/WithdrawalList';
import Bophankinhdoanh from '../pages/Business/Bophankinhdoanh';
import NewsList from '../pages/News/NewsList';
import NewsForm from '../pages/News/NewsForm';
import NewsView from '../pages/News/NewsView';
import Reports from '../pages/Reports/Reports';
import ReferralList from '../pages/Referrals/ReferralList';
import ShippingList from '../pages/Shipping/ShippingList';
import EventCategoryList from '../pages/Events/EventCategoryList';
import EventList from '../pages/Events/EventList';
import EventRegistrations from '../pages/Events/EventRegistrations';
import MembershipPackageList from '../pages/Memberships/MembershipPackageList';
import { authService } from '../services';
import { getUserInfo, clearAuthData } from '../utils/helpers';

const { Header, Sider, Content } = Layout;
const { Title } = Typography;

const AdminLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [openKeys, setOpenKeys] = useState([]);
  const [debugLogs, setDebugLogs] = useState([]);
  const [showDebug, setShowDebug] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setCollapsed(true);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const addDebugLog = (message) => {
    if (process.env.NODE_ENV !== 'development') return;
    const logEntry = `[${new Date().toISOString()}] ${message}`;
    const existingLogs = JSON.parse(sessionStorage.getItem('adminDebugLogs') || '[]');
    existingLogs.push(logEntry);
    if (existingLogs.length > 20) existingLogs.shift();
    sessionStorage.setItem('adminDebugLogs', JSON.stringify(existingLogs));
    setDebugLogs(prev => [...prev.slice(-19), logEntry]);
  };

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      setDebugLogs(JSON.parse(sessionStorage.getItem('adminDebugLogs') || '[]'));
    }
  }, []);

  useEffect(() => {
    const user = getUserInfo();
    if (user) {
      setUserInfo(user);
    } else {
      const timer = setTimeout(() => {
        const userAfterDelay = getUserInfo();
        if (userAfterDelay) setUserInfo(userAfterDelay);
        else navigate('/login');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [navigate]);

  useEffect(() => {
    const pathSegments = location.pathname.split('/');
    if (pathSegments.length > 2) setOpenKeys(['/' + pathSegments[1]]);
  }, [location.pathname]);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .admin-sidebar-scrollable { overflow-y:auto!important; overflow-x:hidden; scrollbar-width:thin; scrollbar-color:rgba(0,0,0,.25) rgba(0,0,0,.05); }
      .admin-sidebar-scrollable::-webkit-scrollbar { width:8px; background:rgba(0,0,0,.03); -webkit-appearance:none; display:block!important; }
      .admin-sidebar-scrollable::-webkit-scrollbar-track { background:rgba(0,0,0,.05); border-radius:4px; margin:4px 0; }
      .admin-sidebar-scrollable::-webkit-scrollbar-thumb { background:rgba(0,0,0,.25); border-radius:4px; min-height:20px; }
      .admin-sidebar-scrollable::-webkit-scrollbar-thumb:hover { background:rgba(0,0,0,.4); }
    `;
    document.head.appendChild(style);
    return () => { if (document.head.contains(style)) document.head.removeChild(style); };
  }, []);

  const handleLogout = async () => {
    try {
      await authService.logout();
      clearAuthData();
      navigate('/login');
      notification.success({ message: 'Đăng xuất thành công', description: 'Hẹn gặp lại bạn lần sau!' });
    } catch (error) {
      clearAuthData();
      navigate('/login');
    }
  };

  const menuItems = [
    { key: '/', icon: <DashboardOutlined />, label: 'Dashboard' },
    { key: '/products', icon: <ShopOutlined />, label: 'Sản phẩm', children: [
      { key: '/products/list', label: 'Danh sách sản phẩm' },
      { key: '/products/add', label: 'Thêm sản phẩm' }
    ]},
    { key: '/categories', icon: <TagsOutlined />, label: 'Phân loại', children: [
      { key: '/categories/list', label: 'Danh sách phân loại' },
      { key: '/categories/add', label: 'Thêm phân loại' }
    ]},
    { key: '/attributes', icon: <SettingOutlined />, label: 'Thuộc tính', children: [
      { key: '/attributes/list', label: 'Danh sách thuộc tính' },
      { key: '/attributes/add', label: 'Thêm thuộc tính' }
    ]},
    { key: '/stock', icon: <InboxOutlined />, label: 'Quản lý kho', children: [
      { key: '/stock/dashboard', label: 'Tổng quan kho' },
      { key: '/stock/list', label: 'Danh sách tồn kho' },
      { key: '/stock/adjustment', label: 'Điều chỉnh kho' }
    ]},
    { key: '/invoices', icon: <FileTextOutlined />, label: 'Hóa đơn', children: [
      { key: '/invoices/export', label: 'Hóa đơn bán hàng' },
      { key: '/invoices/export/add', label: 'Mua hàng tại quầy' },
      { key: '/invoices/import', label: 'Hóa đơn nhập hàng' },
      { key: '/invoices/import/add', label: 'Tạo hóa đơn nhập' }
    ]},
    { key: '/shipping', icon: <CarOutlined />, label: 'Tạo vận đơn' },
    { key: '/users', icon: <UserOutlined />, label: 'Người dùng', children: [
      { key: '/users/list', label: 'Danh sách người dùng' },
      { key: '/users/add', label: 'Thêm người dùng' },
      { key: '/users/pending-approval', label: 'Nhân Sự cần Duyệt' },
      { key: '/referrals', label: 'Người giới thiệu' }
    ]},
    { key: '/approvals', icon: <CheckCircleOutlined />, label: 'Duyệt đơn', children: [
      { key: '/approvals/commission', label: 'Tinh thù lao' },
      { key: '/approvals/packing', label: 'Đóng gói hàng' }
    ]},
    { key: '/business', icon: <BarChartOutlined />, label: 'Phòng Kinh Doanh', children: [
      { key: '/business/department', label: 'Quản lý phòng ban' }
    ]},
    { key: '/withdrawals', icon: <BankOutlined />, label: 'Rút Tiền' },
    { key: '/memberships/packages', icon: <CrownOutlined />, label: 'Gói Thành Viên' },
    { key: '/events', icon: <CalendarOutlined />, label: 'Sự Kiện', children: [
      { key: '/events/list', label: 'Danh Sách Sự Kiện' },
      { key: '/events/categories', label: 'Phân Loại Sự Kiện' }
    ]},
    { key: '/news', icon: <FileTextOutlined />, label: 'Tin tức', children: [
      { key: '/news/list', label: 'Danh sách tin tức' },
      { key: '/news/add', label: 'Thêm tin tức' }
    ]},
    { key: '/reports', icon: <BarChartOutlined />, label: 'Báo cáo', children: [
      { key: '/reports/sales', label: 'Báo cáo bán hàng' },
      { key: '/reports/inventory', label: 'Báo cáo tồn kho' },
      { key: '/reports/customers', label: 'Báo cáo khách hàng' },
      { key: '/reports/revenue', label: 'Báo cáo doanh thu' }
    ]},
    { key: '/settings', icon: <SettingOutlined />, label: 'Cài đặt', children: [
      { key: '/settings/general', label: 'Cài đặt chung' },
      { key: '/settings/email', label: 'Cài đặt email' },
      { key: '/settings/payment', label: 'Cài đặt thanh toán' },
      { key: '/settings/shipping', label: 'Cài đặt vận chuyển' }
    ]}
  ];

  const userMenuItems = [
    { key: 'profile', icon: <UserOutlined />, label: 'Thông tin cá nhân' },
    { key: 'settings', icon: <SettingOutlined />, label: 'Cài đặt' },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Đăng xuất', onClick: handleLogout }
  ];

  const handleMenuClick = ({ key }) => {
    navigate(key);
    if (isMobile) setTimeout(() => setCollapsed(true), 100);
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path.startsWith('/products/view/')) return 'Chi tiết sản phẩm';
    if (path.startsWith('/products/edit/')) return 'Chỉnh sửa sản phẩm';
    if (path.startsWith('/categories/edit/')) return 'Chỉnh sửa phân loại';
    if (path.startsWith('/attributes/edit/')) return 'Chỉnh sửa thuộc tính';
    if (path.startsWith('/attributes/') && path.includes('/values')) return 'Quản lý giá trị thuộc tính';
    if (path.startsWith('/users/view/')) return 'Chi tiết người dùng';
    if (path.startsWith('/news/view/')) return 'Xem chi tiết tin tức';
    if (path.startsWith('/news/edit/')) return 'Chỉnh sửa tin tức';
    if (/^\/events\/\d+\/registrations$/.test(path)) return 'Danh Sách Đăng Ký Sự Kiện';

    const titleMap = {
      '/': 'Dashboard', '/products/list': 'Danh sách sản phẩm', '/products/add': 'Thêm sản phẩm',
      '/categories/list': 'Danh sách phân loại', '/categories/add': 'Thêm phân loại',
      '/attributes/list': 'Danh sách thuộc tính', '/attributes/add': 'Thêm thuộc tính',
      '/stock/dashboard': 'Tổng quan quản lý kho', '/stock/list': 'Danh sách tồn kho', '/stock/adjustment': 'Điều chỉnh kho',
      '/invoices/export': 'Hóa đơn xuất', '/invoices/export/add': 'Tạo hóa đơn xuất', '/invoices/import': 'Hóa đơn nhập', '/invoices/import/add': 'Tạo hóa đơn nhập',
      '/shipping': 'Tạo vận đơn', '/users/list': 'Danh sách người dùng', '/users/add': 'Thêm người dùng',
      '/users/pending-approval': 'Nhân Sự cần Duyệt', '/referrals': 'Danh sách người giới thiệu',
      '/approvals/commission': 'Tinh thù lao', '/approvals/packing': 'Đóng gói hàng',
      '/memberships/packages': 'Quản lý gói thành viên', '/news/list': 'Danh sách tin tức', '/news/add': 'Thêm tin tức',
      '/reports': 'Báo cáo', '/events/list': 'Danh Sách Sự Kiện', '/events/categories': 'Phân Loại Sự Kiện'
    };
    return titleMap[path] || 'Admin Portal';
  };

  if (!userInfo) return null;

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider trigger={null} collapsible collapsed={collapsed} theme="light" breakpoint="lg" collapsedWidth={0}
        style={{ position:'fixed', left: collapsed ? (isMobile ? '-300px' : '0') : '0', top:0, bottom:0, zIndex:1001, width:300, overflow:'hidden', transition:'left .3s', display:'flex', flexDirection:'column' }}>
        <div style={{ height:64, padding:16, display:'flex', alignItems:'center', borderBottom:'1px solid rgba(0,0,0,.08)', background:'rgba(255,255,255,.95)', flexShrink:0 }}>
          <Title level={4} style={{ margin:0, fontWeight:700, fontSize:collapsed ? 14 : 16 }}>{collapsed ? 'AP' : 'Admin Portal'}</Title>
        </div>
        <div className="admin-sidebar-scrollable" style={{ flex:'1 1 auto', overflow:'auto', minHeight:0, height:'calc(100vh - 144px)' }}>
          <Menu theme="light" mode="inline" selectedKeys={[location.pathname]} openKeys={openKeys} onOpenChange={setOpenKeys} items={menuItems} onClick={handleMenuClick} style={{ background:'transparent', border:'none' }} />
        </div>
        <div style={{ position:'absolute', bottom:0, left:0, right:0, height:80, borderTop:'1px solid rgba(0,0,0,.08)', background:'rgba(255,255,255,.95)' }} />
      </Sider>

      <Layout style={{ marginLeft: collapsed ? 0 : (isMobile ? 0 : 250), transition:'margin-left .3s' }}>
        <Header style={{ padding:'0 24px', background:'rgba(255,255,255,.95)', display:'flex', alignItems:'center', justifyContent:'space-between', borderBottom:'1px solid rgba(0,0,0,.08)', position:'sticky', top:0, zIndex:99 }}>
          <div style={{ display:'flex', alignItems:'center' }}>
            {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, { style:{ fontSize:18, cursor:'pointer', color:'#64748b' }, onClick:() => setCollapsed(!collapsed) })}
            <Title level={4} style={{ margin:'0 0 0 16px', fontSize:isMobile ? 16 : 18 }}>{getPageTitle()}</Title>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:isMobile ? 8 : 16 }}>
            <BellOutlined style={{ fontSize:18, color:'#64748b' }} />
            <Dropdown menu={{ items:userMenuItems }} placement="bottomRight" trigger={['click']}>
              <div style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', padding:'4px 8px' }}>
                <Avatar icon={<UserOutlined />} /><span>{userInfo.username}</span>
              </div>
            </Dropdown>
          </div>
        </Header>

        {isMobile && !collapsed && <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)', zIndex:1000 }} onClick={() => setCollapsed(true)} />}

        <Content style={{ margin:0, position:'relative', minHeight:'calc(100vh - 64px)' }}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/products/list" element={<ProductList />} />
            <Route path="/products/add" element={<ProductForm />} />
            <Route path="/products/edit/:id" element={<ProductForm />} />
            <Route path="/products/view/:id" element={<ProductView />} />
            <Route path="/categories/list" element={<CategoryList />} />
            <Route path="/categories/add" element={<CategoryForm />} />
            <Route path="/categories/edit/:id" element={<CategoryForm />} />
            <Route path="/attributes/list" element={<AttributeList />} />
            <Route path="/attributes" element={<AttributeList />} />
            <Route path="/attributes/add" element={<AttributeForm />} />
            <Route path="/attributes/edit/:id" element={<AttributeForm />} />
            <Route path="/attributes/:id/values" element={<AttributeValueManager />} />
            <Route path="/stock/dashboard" element={<StockDashboard />} />
            <Route path="/stock/list" element={<StockList />} />
            <Route path="/stock/adjustment" element={<StockForm />} />
            <Route path="/invoices/export" element={<InvoiceExportList />} />
            <Route path="/invoices/export/add" element={<InvoiceExportForm />} />
            <Route path="/invoices/export/edit/:id" element={<InvoiceExportForm />} />
            <Route path="/invoices/import" element={<InvoiceImportList />} />
            <Route path="/invoices/import/add" element={<InvoiceImportForm />} />
            <Route path="/invoices/import/edit/:id" element={<InvoiceImportForm />} />
            <Route path="/shipping" element={<ShippingList />} />
            <Route path="/users/list" element={<UserList />} />
            <Route path="/users/add" element={<UserForm />} />
            <Route path="/users/view/:id" element={<UserDetail />} />
            <Route path="/users/pending-approval" element={<PendingApproval />} />
            <Route path="/referrals" element={<ReferralList />} />
            <Route path="/approvals/commission" element={<CommissionApproval />} />
            <Route path="/approvals/packing" element={<PackingList />} />
            <Route path="/business/department" element={<Bophankinhdoanh />} />
            <Route path="/withdrawals" element={<WithdrawalList />} />
            <Route path="/memberships/packages" element={<MembershipPackageList />} />
            <Route path="/news/list" element={<NewsList />} />
            <Route path="/news/add" element={<NewsForm />} />
            <Route path="/news/edit/:id" element={<NewsForm />} />
            <Route path="/news/view/:id" element={<NewsView />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/events/list" element={<EventList />} />
            <Route path="/events/:id/registrations" element={<EventRegistrations />} />
            <Route path="/events/categories" element={<EventCategoryList />} />
            <Route path="*" element={<Dashboard />} />
          </Routes>
        </Content>
      </Layout>

      {process.env.NODE_ENV === 'development' && showDebug && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.8)', zIndex:9999, display:'flex', justifyContent:'center', alignItems:'center' }}>
          <div style={{ background:'#fff', padding:20, borderRadius:8, maxWidth:'80%', maxHeight:'80%', overflow:'auto', minWidth:600 }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:20 }}><h3>Debug Logs (Development)</h3><button onClick={() => setShowDebug(false)}>Close</button></div>
            <div style={{ background:'#f5f5f5', padding:10, fontFamily:'monospace', fontSize:12 }}>{debugLogs.length === 0 ? 'No logs available...' : debugLogs.map((log,index) => <div key={index}>{log}</div>)}</div>
            <div style={{ marginTop:20, textAlign:'center' }}><button onClick={() => { sessionStorage.removeItem('adminDebugLogs'); setDebugLogs([]); }}>Clear Logs</button></div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AdminLayout;
